import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DollarSign,
  Download,
  TrendingUp,
  Building2,
  ArrowLeft,
  RefreshCw,
  Calendar,
} from "lucide-react";
import { toast } from "react-hot-toast";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminCommissionReports() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Set default dates (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateTo(today.toISOString().split("T")[0]);
    setDateFrom(thirtyDaysAgo.toISOString().split("T")[0]);

    // Load initial report
    fetchReport(
      thirtyDaysAgo.toISOString().split("T")[0],
      today.toISOString().split("T")[0]
    );
  }, []);

  const fetchReport = async (from, to) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/super-admin/reports/commission`,
        {
          date_from: from || dateFrom,
          date_to: to || dateTo,
        },
        { headers: getAdminAuthHeader() }
      );
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to load commission report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Please select date range");
      return;
    }
    fetchReport(dateFrom, dateTo);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <SuperAdminLayout>
      <div>
        <div
          className="mb-8 flex  w-full items-center px-6
  bg-gradient-to-r from-orange-500 to-red-500
  shadow-md text-white py-[1.65rem]"
        >
          <Button
            onClick={() => navigate("/admin/dashboard")}
            variant="ghost"
            className="hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold ">Commission Reports</h1>
            <p className=" mt-1">
              Track platform earnings and restaurant-wise commission breakdown
            </p>
          </div>
        </div>
        <div className="p-8">
          {/* Date Range Selector */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    placeholder="From"
                  />
                </div>
                <span className="text-gray-500">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="To"
                />
                <Button
                  onClick={handleGenerateReport}
                  disabled={loading}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Generate Report
                </Button>
                {/* <Button variant="outline" disabled>
                  <Download className="h-4 w-4 mr-2" />
                  Export Report
                </Button> */}
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">Loading report data...</p>
            </div>
          ) : reportData ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Total Commission
                    </CardTitle>
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(reportData.total_commission)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Platform earnings
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Total Revenue
                    </CardTitle>
                    <TrendingUp className="h-5 w-5 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">
                      {formatCurrency(reportData.total_revenue)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Gross sales</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Avg Commission Rate
                    </CardTitle>
                    <TrendingUp className="h-5 w-5 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">
                      {reportData.avg_commission_rate.toFixed(1)}%
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Average rate</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Active Restaurants
                    </CardTitle>
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">
                      {reportData.restaurant_count}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Generating revenue
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Restaurant Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Restaurant-wise Commission Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {reportData.restaurant_breakdown &&
                  reportData.restaurant_breakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                              Restaurant
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                              Orders
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                              Revenue
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                              Rate
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                              Commission
                            </th>
                            {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                              Status
                            </th> */}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {reportData.restaurant_breakdown.map((restaurant) => (
                            <tr
                              key={restaurant.restaurant_id}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-4 py-4">
                                <div className="font-medium text-gray-900">
                                  {restaurant.restaurant_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {restaurant.restaurant_id.substring(0, 8)}...
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                {restaurant.total_orders}
                              </td>
                              <td className="px-4 py-4">
                                <div className="font-semibold text-gray-900">
                                  {formatCurrency(restaurant.total_revenue)}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                {restaurant.commission_percentage}%
                              </td>
                              <td className="px-4 py-4">
                                <div className="font-bold text-green-600">
                                  {formatCurrency(restaurant.commission_amount)}
                                </div>
                              </td>
                              {/* <td className="px-4 py-4">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                  {restaurant.payment_status.toUpperCase()}
                                </span>
                              </td> */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <DollarSign className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        No Commission Data
                      </h3>
                      <p className="text-gray-600 max-w-md mx-auto">
                        No orders found in the selected date range.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
